import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Question } from "@/types/test";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

interface TestQuestionProps {
  question: Question;
  selectedAnswer?: number;
  onAnswerSelect: (questionId: number, answerIndex: number, value?: string) => void;
}

export const TestQuestion = ({ question, selectedAnswer, onAnswerSelect }: TestQuestionProps) => {
  const [date, setDate] = useState<Date>();
  const [inputValue, setInputValue] = useState<string>("");
  const [imageError, setImageError] = useState(false);
  const { toast } = useToast();

  // Reset state when question changes
  useEffect(() => {
    setDate(undefined);
    setInputValue("");
    setImageError(false);
  }, [question.id]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    onAnswerSelect(question.id, 0, value);
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    setDate(selectedDate);
    if (selectedDate) {
      onAnswerSelect(question.id, 0, format(selectedDate, 'yyyy-MM-dd'));
    }
  };

  const handleImageError = () => {
    console.error(`Image failed to load for question ${question.id}:`, question.imageUrl);
    setImageError(true);
    toast({
      title: "Image Loading Error",
      description: "Failed to load question image. Please continue with the test.",
      variant: "destructive",
    });
  };

  if (!question) {
    return null;
  }

  const renderImage = (url?: string) => {
    if (!url || imageError) return null;
    
    console.log("Rendering image with URL:", url); // Debug log

    return (
      <div className="flex justify-center mb-4">
        <img 
          src={url}
          alt="Question illustration"
          className="max-h-48 object-contain rounded-lg shadow-md"
          onError={handleImageError}
          loading="eager"
        />
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-4">
        {question.imageUrl && renderImage(question.imageUrl)}
        <h2 className="text-xl font-semibold">{question.text}</h2>
        
        {/* Render multiple images if available */}
        {question.imageUrls && question.imageUrls.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {question.imageUrls.map((url, index) => (
              <div key={index} className="flex flex-col items-center">
                <img 
                  src={url}
                  alt={`Option ${index + 1}`}
                  className="max-h-64 object-contain rounded-lg shadow-md"
                  onError={handleImageError}
                  loading="eager"
                />
              </div>
            ))}
          </div>
        )}
      </div>
      
      {question.isInputField ? (
        <div className="space-y-4">
          <Label>{question.options[0]}</Label>
          {question.inputType === 'date' ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={handleDateSelect}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Input
              type="text"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              className="w-full"
              placeholder="Type your answer here..."
            />
          )}
        </div>
      ) : (
        <RadioGroup
          value={selectedAnswer?.toString()}
          onValueChange={(value) => {
            const selectedOption = question.options[parseInt(value)];
            onAnswerSelect(question.id, parseInt(value), selectedOption);
          }}
          className="space-y-4"
        >
          {question.options.map((option, optionIndex) => {
            const optionImage = question.imageUrls?.[optionIndex];
            
            return (
              <div 
                key={optionIndex} 
                className={cn(
                  "flex items-center space-x-3 p-4 rounded-lg border-2 border-transparent transition-all",
                  "hover:border-primary/50 cursor-pointer",
                  selectedAnswer === optionIndex && "border-primary bg-primary/5"
                )}
                onClick={() => onAnswerSelect(question.id, optionIndex, option)}
              >
                <RadioGroupItem 
                  value={optionIndex.toString()} 
                  id={`q${question.id}-a${optionIndex}`}
                  className="mr-2"
                />
                
                <div className="flex items-center gap-4 flex-1">
                  {optionImage && renderImage(optionImage)}
                  <Label 
                    htmlFor={`q${question.id}-a${optionIndex}`}
                    className="text-base cursor-pointer flex-1"
                  >
                    {option}
                  </Label>
                </div>
              </div>
            );
          })}
        </RadioGroup>
      )}
    </div>
  );
};
